<template>
  <div class="mx-auto max-w-screen-xl p-4">
    <h1 class="text-center">專業詞彙</h1>
    <div class="sm:w-1/3 w-1/2 py-4">
      <UInput
        v-model="q"
        placeholder="關鍵字搜尋"
        color="blue"
        icon="i-heroicons-magnifying-glass-20-solid"
        trailing
        size="lg"
      />
    </div>

    <UTable
      :rows="paginatedRows"
      :columns="columns"
      :ui="{
        tr: { base: 'even:bg-white', active: 'hover:bg-neutral-50' },
        th: {
          base: 'text-neutral-50',
          size: 'text-base font-bold',
          color: 'text-neutral-50  dark:text-neutral-50'
        },
        td: { size: 'text-base', color: 'text-neutral-800' }
      }"
      :sort-button="{ color: 'neutral-50' }"
      :empty-state="{ label: '沒有符合條件的資料' }"
    />
    <div class="flex justify-center py-4">
      <UPagination v-model="selectedPage" :total="filteredRows.length" :page-count="pageCount" />
    </div>
  </div>
</template>

<script setup lang="ts">
const selectedPage = ref(1)
const pageCount = 20
const q = ref('')
const columns = [
  {
    key: 'name',
    label: '詞彙',
    sortable: true
  },
  {
    key: 'explanation',
    label: '說明'
  }
]

const glossary = [
  {
    name: '不動產投資信託 (REITs)',
    explanation: '以不動產為投資標的，將其證券化後發行給投資人的工具'
  },
  {
    name: '不動產抵押債權證券 (MBS)',
    explanation: '以住房抵押貸款為基礎發行的證券'
  },
  {
    name: '不動產證券化型基金',
    explanation: '將不動產證券化後進行投資的基金'
  },
  {
    name: '公債、國庫券',
    explanation: '政府發行的一種債券，通常被視為最安全的投資'
  },
  {
    name: '市場風險',
    explanation: '由於市場整體波動而產生的風險。'
  },
  {
    name: '市場時機策略',
    explanation: '根據市場變化，主動調整投資組合的策略'
  },
  {
    name: '市場變化',
    explanation: '市場價格的波動'
  },
  {
    name: '平衡型基金',
    explanation: '將股票與債券混合投資的基金'
  },
  {
    name: '平衡型策略',
    explanation: '兼顧風險與收益，追求穩定增值的投資策略'
  },
  {
    name: '申購基金手續費',
    explanation: '購買基金時支付的費用'
  },
  {
    name: '目標保險費',
    explanation: '保持保單效力所需的最低保費'
  },
  {
    name: '交易處理',
    explanation: '包括買賣證券、支付費用等交易的處理。'
  },
  {
    name: '全權委託帳戶',
    explanation: '將投資決策權交給專業投資機構的帳戶'
  },
  {
    name: '再平衡',
    explanation: '定期調整投資組合，以維持原先設定的資產配置比例。'
  },
  {
    name: '多元化策略',
    explanation: '將投資分散於不同資產類別，降低風險的策略'
  },
  {
    name: '多重資產型基金',
    explanation: '投資於多種資產類別的基金'
  },
  {
    name: '成本基礎',
    explanation: '投資資產的原始購買成本。'
  },
  {
    name: '投信公司',
    explanation: '負責管理基金、代客理財的專業機構。'
  },
  {
    name: '投資目標',
    explanation: '投資希望達成的目標，如退休、子女教育等'
  },
  {
    name: '投資收益',
    explanation: '投資資產帶來的收益，包括股息、利息、資本利得等。'
  },
  {
    name: '投資型保單',
    explanation: '結合保障與投資功能的保險商品，保戶可選擇不同投資標的。'
  },
  {
    name: '投資相關費用',
    explanation: '與投資相關的費用，如基金申購、保管、贖回等費用'
  },
  {
    name: '投資風險',
    explanation: '投資可能產生虧損的風險'
  },
  {
    name: '投資區域',
    explanation: '基金投資的國家或地區'
  },
  {
    name: '投資帳戶',
    explanation: '保單帳戶中的資金投入不同的投資標的'
  },
  {
    name: '投資帳戶價值',
    explanation: '投資型保單中，投資部分的帳戶價值，會隨著市場波動而變動'
  },
  {
    name: '投資組合',
    explanation: '由多種投資標的組成的組合，用以分散風險。'
  },
  {
    name: '投資策略',
    explanation: '根據投資目標和風險承受能力，選擇的投資方向和方式'
  },
  {
    name: '投資費用',
    explanation: '與投資相關的費用，如經理費、申購費等。'
  },
  {
    name: '投資損失',
    explanation: '投資資產帶來的損失。'
  },
  {
    name: '投資資產',
    explanation: '保戶投入的資金所購買的各種金融資產，如股票、債券、基金等。'
  },
  {
    name: '投資標的',
    explanation: '投資帳戶中所投資的具體項目，如股票、債券、基金等'
  },
  {
    name: '身故給付',
    explanation: '被保險人身故時，保險公司給付受益人的金額'
  },
  {
    name: '受益人',
    explanation: '保險金的收受人。'
  },
  {
    name: '股票',
    explanation: '公司發行給股東的證券，代表對公司的所有權'
  },
  {
    name: '股票型基金',
    explanation: '主要投資於股票的基金'
  },
  {
    name: '金融債券',
    explanation: '金融機構發行的一種債券'
  },
  {
    name: '金融資產證券化型基金',
    explanation: '將金融資產證券化後進行投資的基金'
  },
  {
    name: '金融資產證券化商品',
    explanation: '將金融資產（如貸款、應收帳款）轉化為可交易證券的工具'
  },
  {
    name: '保本型基金',
    explanation: '保證本金安全的基金'
  },
  {
    name: '保守型策略',
    explanation: '以低風險、穩定收益為目標的投資策略'
  },
  {
    name: '保單借款',
    explanation: '保戶以保單價值為抵押，向保險公司借款。'
  },
  {
    name: '保單帳戶',
    explanation: '保費扣除費用後，存入專屬的帳戶，作為投資本金'
  },
  {
    name: '保單條款',
    explanation: '規範保戶與保險公司權益義務的契約'
  },
  {
    name: '保單現金價值',
    explanation: '保單帳戶的累積價值，反映投資績效'
  },
  {
    name: '保單管理費',
    explanation: '保險公司收取的管理費用，用於支付保單管理、行政等成本'
  },
  {
    name: '保單價值',
    explanation: '投資型保單的帳戶價值，會隨著投資績效而變動。'
  },
  {
    name: '保費',
    explanation: '保戶向保險公司支付的費用，用以換取保險保障。'
  },
  {
    name: '保障範圍',
    explanation: '保險公司承諾給付保險金的範圍。'
  },
  {
    name: '保險成本',
    explanation: '提供保障所需的成本，如死亡給付、殘疾給付等'
  },
  {
    name: '保險期間',
    explanation: '保險契約生效的期間。'
  },
  {
    name: '保險顧問',
    explanation: '提供保險諮詢的專業人士。'
  },
  {
    name: '保額',
    explanation: '保險公司承諾給付的保險金金額。'
  },
  {
    name: '保證最低身故金額',
    explanation: '保險公司保證在被保險人身故時，受益人至少能領取的金額'
  },
  {
    name: '信用風險',
    explanation: '投資標的違約而導致損失的風險。'
  },
  {
    name: '指數型基金 (ETF)',
    explanation: '追蹤特定指數的基金'
  },
  {
    name: '流動性風險',
    explanation: '投資標的無法迅速變現而產生的風險。'
  },
  {
    name: '風險',
    explanation: '投資可能產生損失的可能性。'
  },
  {
    name: '風險承受度',
    explanation: '個體能承受投資損失的能力。'
  },
  {
    name: '風險管理',
    explanation: '識別、評估和控制投資風險的過程。'
  },
  {
    name: '除外責任',
    explanation: '保險公司不承擔賠償責任的範圍。'
  },
  {
    name: '基金',
    explanation: '將眾多投資人的資金集合起來，委託專業經理人進行投資的工具'
  },
  {
    name: '基金保管費',
    explanation: '用於保管基金資產的費用'
  },
  {
    name: '基金風險報酬等級',
    explanation: '依據基金投資標的、地區、投資策略等因素，對基金的風險與潛在收益進行分級'
  },
  {
    name: '基金經理費',
    explanation: '基金管理公司收取的管理費用'
  },
  {
    name: '基金轉換費用',
    explanation: '在不同基金之間轉換時支付的費用'
  },
  {
    name: '基金贖回費用',
    explanation: '贖回基金時支付的費用'
  },
  {
    name: '專設帳簿',
    explanation: '為每位投資型保戶獨立設置的帳戶，用於管理其投資資產。'
  },
  {
    name: '淨資產價值 (NAV)',
    explanation: '扣除相關費用後，投資資產的實際價值。'
  },
  {
    name: '理財規劃師',
    explanation: '提供專業的理財建議的專業人士'
  },
  {
    name: '組合型基金',
    explanation: '由多個子基金組成的基金'
  },
  {
    name: '被保險人',
    explanation: '保險契約中受到保障的人。'
  },
  {
    name: '貨幣市場型基金',
    explanation: '主要投資於短期債券和貨幣市場工具的基金'
  },
  {
    name: '部分提領費用',
    explanation: '部分提領保單現金價值時支付的費用'
  },
  {
    name: '部分贖回',
    explanation: '保戶從專設帳戶中提取部分資金。'
  },
  {
    name: '最低保證價值',
    explanation: '保險公司保證保單價值不會低於某一特定金額。'
  },
  {
    name: '報酬',
    explanation: '投資可能獲得的收益。'
  },
  {
    name: '期繳',
    explanation: '將保費分期繳納'
  },
  {
    name: '等待期',
    explanation: '某些疾病或傷害需經過一段時間才會給付保險金。'
  },
  {
    name: '結構型商品',
    explanation: '將不同金融工具結合在一起，設計出具有特定風險報酬特性的金融產品'
  },
  {
    name: '超額保險費',
    explanation: '超過目標保險費的部分，可用於增加保單現金價值或投資收益'
  },
  {
    name: '傳統壽險',
    explanation: '純粹提供保障的保險商品，主要保障身故、失能等風險。'
  },
  {
    name: '債券',
    explanation: '政府或公司發行的一種債務證券，代表債權人向發行人貸款'
  },
  {
    name: '債券型基金',
    explanation: '主要投資於債券的基金'
  },
  {
    name: '會計處理',
    explanation: '根據會計準則，對專設帳戶進行會計處理，產生財務報表。'
  },
  {
    name: '經濟走勢',
    explanation: '宏觀經濟的變化趨勢'
  },
  {
    name: '解約費用',
    explanation: '解約保單時支付的費用'
  },
  {
    name: '資訊不對稱風險',
    explanation: '投資者可能對產品不了解，做出錯誤的投資決策'
  },
  {
    name: '資訊系統',
    explanation: '用於記錄、管理和查詢專設帳戶資訊的系統。'
  },
  {
    name: '資產保管',
    explanation: '由獨立的保管機構保管投資資產，確保資產安全。'
  },
  {
    name: '資產配置',
    explanation: '將資金分配到不同類型的資產。'
  },
  {
    name: '資產價值',
    explanation: '投資資產在特定時點的市場價值。'
  },
  {
    name: '槓桿/反向型ETF',
    explanation: '利用金融衍生工具放大或反向追蹤指數的基金'
  },
  {
    name: '管理費',
    explanation: '投信公司收取的管理費用。'
  },
  {
    name: '銀行存款',
    explanation: '將資金存入銀行，獲得利息的投資方式'
  },
  {
    name: '彈性繳費',
    explanation: '可彈性調整繳費金額和時間'
  },
  {
    name: '積極型策略',
    explanation: '追求高潛在回報，但風險較高的投資策略'
  },
  {
    name: '績效評估',
    explanation: '對投資績效進行評估。'
  },
  {
    name: '績效費用',
    explanation: '當投資績效超越某一基準時，投信公司額外收取的費用。'
  },
  {
    name: '鎖定期',
    explanation: '保戶無法隨時贖回保單的期間。'
  },
  {
    name: '類全委保單',
    explanation: '將投資決策委託給專業投資團隊管理的保險商品。'
  },
  {
    name: '類全委帳戶',
    explanation: '由專業機構代為管理的投資帳戶'
  },
  {
    name: '躉繳',
    explanation: '一次性繳清所有保費'
  },
  {
    name: '變額年金保險',
    explanation: '一種投資型保單，透過定期定額的方式累積資金，以達到退休或其他人生目標'
  },
  {
    name: '變額年金險',
    explanation: '用於退休金規劃的投資型保單'
  },
  {
    name: '變額萬能壽險',
    explanation: '變額壽險的延伸版本，具有更靈活的保單設計'
  },
  {
    name: '變額壽險',
    explanation: '保障金額隨投資績效變動的投資型保單'
  },
  {
    name: 'GLWB (Guaranteed Lifetime Withdrawal Benefit)',
    explanation: '保證終身提領金額'
  },
  {
    name: 'GMAB (Guarantee Minimum Accumulation Benefit)',
    explanation: '保證最低累積帳戶價值'
  },
  {
    name: 'GMDB (Guarantee Minimum Death Benefit)',
    explanation: '保證最低身故給付'
  },
  {
    name: 'GMWB (Guarantee Minimum Withdrawal Benefit)',
    explanation: '保證最低提領金額'
  },
  {
    name: 'RR等級',
    explanation: '風險報酬等級，數字越大，風險越高，潛在收益也越高'
  }
]

const total = computed(() => Math.ceil(filteredRows.value.length / pageCount))

const paginatedRows = computed(() => {
  const start = (selectedPage.value - 1) * pageCount
  return filteredRows.value.slice(start, start + pageCount)
})

const filteredRows = computed(() => {
  if (!q.value) {
    return glossary
  }

  return glossary.filter((item) => {
    return Object.values(item).some((value) => {
      return String(value).toLowerCase().includes(q.value.toLowerCase())
    })
  })
})

definePageMeta({
  title: '專業詞彙',
  description: '有關投資型保單及類全委保的專業詞彙'
})
</script>
